import * as React from "react";
import { ReactNode } from "react";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Switch } from "@volca/ui/components/ui/switch";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";

type SwitchProps = React.ComponentProps<typeof Switch>;

const SWITCH_SIZE = {
  default: "default",
  wideTrack: "default",
} as const satisfies Record<string, SwitchProps["size"]>;

interface SwitchWithLabelProps
  extends Omit<SwitchProps, "checked" | "onCheckedChange" | "onChange"> {
  variant?: keyof typeof SWITCH_SIZE | null;
  label: string;
  labelClassName?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: ReactNode | null;
  disabledTooltip?: ReactNode | string;
}

const SwitchWithLabelBase = ({
  label,
  labelClassName,
  checked,
  onChange,
  description,
  variant,
  className,
  id,
  ...props
}: Omit<SwitchWithLabelProps, "disabledTooltip">) => {
  const generatedId = React.useId();
  const switchId = id ?? generatedId;
  return (
    <Stack align="start" gap={0}>
      <Stack direction="row" align="center" gap={0}>
        <Stack align="start" gap={4} className="p-3 pl-0">
          <Switch
            id={switchId}
            checked={checked}
            onCheckedChange={onChange}
            size={SWITCH_SIZE[variant ?? "default"]}
            className={className}
            {...props}
          />
        </Stack>
        <Label htmlFor={switchId} className={labelClassName}>
          {label}
        </Label>
      </Stack>
      {!!description && (
        <Text variant="caption" tone="muted">
          {description}
        </Text>
      )}
    </Stack>
  );
};

export const SwitchWithLabel = ({
  disabledTooltip,
  ...props
}: SwitchWithLabelProps) => {
  if (disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <div>
              <SwitchWithLabelBase {...props} />
            </div>
          }
        />
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return <SwitchWithLabelBase {...props} />;
};
